import React from 'react';
import { Link } from 'react-router-dom';
import { Wrench, CheckCircle, ArrowRight, ExternalLink, Phone, Mail, Clock, Shield, Star, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const RepairServices = () => {
  const repairServices = [
    {
      title: 'Complete Rebuild',
      description: 'Total freeze dryer rebuild to enhance performance and correct engineering challenges',
      icon: Package
    },
    {
      title: 'Refrigeration Service',
      description: 'Compressor diagnostics, refrigerant recharge, and system repairs',
      icon: Wrench
    },
    {
      title: 'Control Board Repair',
      description: 'Electronic diagnostics, board replacement, and software updates',
      icon: Shield
    },
    {
      title: 'Preventive Maintenance',
      description: 'Full system inspection, cleaning, and tune-up services',
      icon: CheckCircle
    }
  ];

  return (
    <div className="min-h-screen bg-[#f0f4f8]" data-testid="repair-services-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-44 pb-20 bg-gradient-to-br from-[#003456] to-[#001d30] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-[#fdc31c] text-[#0a0203] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Wrench className="h-4 w-4" />
              Expert Repair Services
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="repair-headline">
              Freeze Dryer
              <span className="block text-[#fdc31c]">Rebuild & Repair Services</span>
            </h1>
            
            <p className="text-xl text-white/80 mb-8 max-w-2xl">
              Professional freeze dryer rebuild and repair services for all major brands. 
              We specialize in enhancing performance and addressing engineering challenges to extend the life of your equipment.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <a href="https://www.lyphlinefreezedry.com/" target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#fdc31c] hover:bg-[#e5b019] text-[#0a0203] font-semibold px-8 py-6 text-lg" data-testid="lyphline-cta">
                  Visit LyphLine <ExternalLink className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <Link to="/repair/quote">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#003456] px-8 py-6 text-lg">
                  Request Repair Quote
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-[#f0f4f8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0a0203] mb-4">Our Repair Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive repair and maintenance services to keep your freeze dryer running at peak performance
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {repairServices.map((service, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-[#dbeef5] rounded-xl flex items-center justify-center mb-6">
                  <service.icon className="h-7 w-7 text-[#003456]" />
                </div>
                <h3 className="text-xl font-semibold text-[#0a0203] mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LyphLine CTA Section */}
      <section className="py-20 bg-[#003456]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Professional Rebuild Services
                <span className="block text-[#fdc31c]">For All Major Brands</span>
              </h2>
              <p className="text-white/80 mb-8 text-lg">
                We offer complete freeze dryer rebuilds rather than simple repairs. Each machine has unique engineering 
                challenges, and our team works to correct them. We can rebuild to enhance performance, giving your 
                equipment new life with improved functionality.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3 text-white">
                  <CheckCircle className="h-5 w-5 text-[#fdc31c]" />
                  <span>Complete system rebuild and enhancement</span>
                </div>
                <div className="flex items-center gap-3 text-white">
                  <CheckCircle className="h-5 w-5 text-[#fdc31c]" />
                  <span>Engineering challenge corrections</span>
                </div>
                <div className="flex items-center gap-3 text-white">
                  <CheckCircle className="h-5 w-5 text-[#fdc31c]" />
                  <span>Performance enhancement modifications</span>
                </div>
                <div className="flex items-center gap-3 text-white">
                  <CheckCircle className="h-5 w-5 text-[#fdc31c]" />
                  <span>90-day rebuild warranty</span>
                </div>
              </div>
              
              <a href="https://www.lyphlinefreezedry.com/" target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#fdc31c] hover:bg-[#e5b019] text-[#0a0203] font-semibold px-8 py-6 text-lg">
                  Go to LyphLine.com <ExternalLink className="ml-2 h-5 w-5" />
                </Button>
              </a>
            </div>
            
            <div className="bg-white/10 rounded-2xl p-8 backdrop-blur-sm">
              <h3 className="text-xl font-bold text-white mb-6">Contact Our Repair Team</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#fdc31c] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 text-[#0a0203]" />
                  </div>
                  <div>
                    <p className="text-white/70 text-sm">Call Us</p>
                    <a href="tel:1-800-555-0199" className="text-white text-lg font-semibold hover:text-[#fdc31c]">
                      1-800-555-0199
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#fdc31c] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-[#0a0203]" />
                  </div>
                  <div>
                    <p className="text-white/70 text-sm">Email Us</p>
                    <a href="mailto:repair@lyphline.com" className="text-white text-lg font-semibold hover:text-[#fdc31c]">
                      repair@lyphline.com
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#fdc31c] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-[#0a0203]" />
                  </div>
                  <div>
                    <p className="text-white/70 text-sm">Business Hours</p>
                    <p className="text-white text-lg font-semibold">Mon-Fri: 8am - 5pm EST</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-[#0a0203] text-center mb-12">Repair FAQs</h2>
          
          <div className="space-y-6">
            <div className="bg-[#f0f4f8] rounded-xl p-6">
              <h3 className="font-semibold text-[#0a0203] mb-2">How do I know if my freeze dryer needs repair?</h3>
              <p className="text-gray-600">
                Common signs include unusual noises, longer cycle times, incomplete freeze drying, 
                error codes, or visible leaks. If you notice any of these, contact us for a diagnostic assessment.
              </p>
            </div>
            
            <div className="bg-[#f0f4f8] rounded-xl p-6">
              <h3 className="font-semibold text-[#0a0203] mb-2">Do you repair all freeze dryer brands?</h3>
              <p className="text-gray-600">
                Yes! Our technicians are trained to retrofit all major freeze dryer brands. We can work on 
                equipment from any manufacturer and address most common issues through our rebuild process.
              </p>
            </div>
            
            <div className="bg-[#f0f4f8] rounded-xl p-6">
              <h3 className="font-semibold text-[#0a0203] mb-2">What is the typical repair turnaround time?</h3>
              <p className="text-gray-600">
                Turnaround time is calculated after the customer approves the repair plan. Once approved, 
                most rebuilds are completed within 5-10 business days. Rush service is available for an additional fee.
              </p>
            </div>
            
            <div className="bg-[#f0f4f8] rounded-xl p-6">
              <h3 className="font-semibold text-[#0a0203] mb-2">What's the difference between repair and rebuild?</h3>
              <p className="text-gray-600">
                We offer total rebuilds rather than simple repairs. Each freeze dryer has unique engineering 
                challenges, and our team attempts to correct them. We can rebuild to enhance performance, 
                but we cannot reengineer the fundamental machine design.
              </p>
            </div>
            
            <div className="bg-[#f0f4f8] rounded-xl p-6">
              <h3 className="font-semibold text-[#0a0203] mb-2">Do you offer on-site repairs?</h3>
              <p className="text-gray-600">
                For commercial units, we offer on-site service in select areas. 
                Contact us for availability and pricing in your location.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RepairServices;
